// Product detail drawer — slides in from the right on catalogue when
// a member taps "LEARN MORE →" on any research SKU card.
//
// Renders whichever documentation fields the BE has populated for the
// SKU. Every section is optional; missing fields hide their section
// (no empty "N/A" placeholders). Rendering order:
//   1. Hero — name, ingredient, batch ID, availability, price
//   2. Video (if `videoUrl`)
//   3. Description
//   4. Benefits (bullet list)
//   5. Mechanism
//   6. Dosing
//   7. Contraindications (warning band)
//   8. References (linked list to peer-reviewed studies)
//   9. Attachments (downloadable files — COAs, batch certs, etc.)
//
// Accessible dialog: escape closes, click outside closes, focus trap
// via `role="dialog"` and body-scroll-lock while open.
import { useEffect } from "react";
import Button from "@/components/Button";
import { C } from "@/lib/brand";
import { fmtMoney } from "@/lib/format";
import { RESEARCH_NAVY, RESEARCH_NAVY_DEEP } from "@/components/research/ResearchHero";

const AVAILABILITY_LABEL = {
  in_stock: "IN STOCK",
  backorder: "BACKORDER",
  out_of_stock: "OUT OF STOCK",
  coming_soon: "COMING SOON",
};

export default function ProductDetailDrawer({ item, isOpen, onClose, onAdd, isInTray, onNotify }) {
  // Body-scroll lock + escape handler while drawer is open.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !item) return null;

  const available = item.availability === "in_stock" || item.availability === "backorder";
  const availabilityLabel = AVAILABILITY_LABEL[item.availability] || "UNAVAILABLE";
  const priceCents = item.monthlyPrice ?? item.oneTimePrice;
  const priceLabel = priceCents != null
    ? fmtMoney(priceCents / 100, (item.currency || "usd").toUpperCase())
    : "—";
  const priceSuffix = item.monthlyPrice != null ? "/ month" : "/ vial";

  return (
    <div data-testid={`product-drawer-${item.sku}`} role="dialog" aria-modal="true" aria-labelledby="drawer-title">
      {/* Scrim */}
      <div
        onClick={onClose}
        data-testid="product-drawer-scrim"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,26,38,0.55)",
          zIndex: 90,
          animation: "fadeIn 220ms ease",
        }}
      />
      {/* Panel */}
      <aside
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(560px, 100vw)",
          background: C.cream,
          zIndex: 91,
          boxShadow: "-30px 0 60px rgba(15,26,38,0.25)",
          overflowY: "auto",
          animation: "slideInR 260ms cubic-bezier(0.22, 0.61, 0.36, 1)",
        }}
      >
        {/* Sticky header */}
        <div
          style={{
            position: "sticky",
            top: 0,
            background: C.cream,
            borderBottom: `1px solid ${C.warmGray4}`,
            padding: "16px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: 2,
          }}
        >
          <span
            style={{
              fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
              fontSize: 10,
              letterSpacing: "0.22em",
              color: RESEARCH_NAVY,
              textTransform: "uppercase",
            }}
          >
            {item.batchId || item.sku}
          </span>
          <button
            type="button"
            onClick={onClose}
            data-testid="product-drawer-close"
            aria-label="Close product details"
            style={{
              background: "transparent",
              border: 0,
              fontSize: 22,
              lineHeight: 1,
              color: RESEARCH_NAVY,
              cursor: "pointer",
              padding: 4,
            }}
          >
            ✕
          </button>
        </div>

        {/* Hero */}
        <section style={{ padding: "32px 24px 24px", borderBottom: `1px solid ${C.warmGray4}` }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: available ? RESEARCH_NAVY : C.peachDark }}>
            ▪ {availabilityLabel}
          </div>
          <h2
            id="drawer-title"
            style={{
              margin: "10px 0 0",
              fontSize: 34,
              fontWeight: 800,
              letterSpacing: "-0.02em",
              lineHeight: 1.05,
              color: RESEARCH_NAVY_DEEP,
              fontFamily: '"DM Sans", sans-serif',
              textTransform: "uppercase",
            }}
          >
            {item.name}
          </h2>
          {item.ingredient && item.ingredient !== item.name && (
            <div
              style={{
                marginTop: 8,
                fontSize: 15,
                color: C.warmGray,
                fontStyle: "italic",
                fontFamily: '"Instrument Serif", Georgia, serif',
              }}
            >
              {item.ingredient}
            </div>
          )}
          <div style={{ marginTop: 22, display: "flex", alignItems: "baseline", gap: 10 }}>
            <span style={{ fontSize: 30, fontWeight: 800, color: RESEARCH_NAVY_DEEP, letterSpacing: "-0.02em" }}>
              {priceLabel}
            </span>
            <span style={{ fontSize: 11, letterSpacing: "0.18em", color: C.warmGray, textTransform: "uppercase" }}>
              {priceSuffix}
            </span>
          </div>
        </section>

        {/* Body sections — all conditional. */}
        <div style={{ padding: "24px" }}>
          {item.videoUrl && (
            <Section title="Explainer" testId="product-drawer-video">
              <VideoEmbed url={item.videoUrl} sku={item.sku} />
            </Section>
          )}
          {item.description && (
            <Section title="Overview" testId="product-drawer-description">
              <p style={bodyText}>{item.description}</p>
            </Section>
          )}
          {Array.isArray(item.benefits) && item.benefits.length > 0 && (
            <Section title="Benefits" testId="product-drawer-benefits">
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {item.benefits.map((b, i) => (
                  <li
                    key={i}
                    data-testid={`product-drawer-benefit-${i}`}
                    style={{
                      padding: "10px 0 10px 22px",
                      position: "relative",
                      fontSize: 15,
                      color: RESEARCH_NAVY_DEEP,
                      lineHeight: 1.55,
                      borderBottom: i < item.benefits.length - 1 ? `1px solid ${C.warmGray4}` : "none",
                    }}
                  >
                    <span style={{ position: "absolute", left: 0, top: 12, color: RESEARCH_NAVY, fontSize: 10 }}>▪</span>
                    {b}
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {item.mechanism && (
            <Section title="Mechanism" testId="product-drawer-mechanism">
              <p style={bodyText}>{item.mechanism}</p>
            </Section>
          )}
          {item.dosing && (
            <Section title="Typical Protocol" testId="product-drawer-dosing">
              <p style={bodyText}>{item.dosing}</p>
            </Section>
          )}
          {item.contraindications && (
            <Section title="Contraindications" testId="product-drawer-contraindications">
              <div
                style={{
                  padding: 16,
                  background: "rgba(232,135,88,0.08)",
                  border: `1px solid ${C.peachDark}`,
                  color: RESEARCH_NAVY_DEEP,
                  fontSize: 14,
                  lineHeight: 1.6,
                }}
              >
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDark, marginBottom: 8 }}>
                  ⚠ SAFETY NOTICE
                </div>
                {item.contraindications}
              </div>
            </Section>
          )}
          {Array.isArray(item.references) && item.references.length > 0 && (
            <Section title="References" testId="product-drawer-references">
              <ol style={{ margin: 0, padding: 0, listStyle: "none", counterReset: "ref" }}>
                {item.references.map((r, i) => (
                  <li
                    key={i}
                    style={{
                      padding: "10px 0",
                      borderBottom: i < item.references.length - 1 ? `1px solid ${C.warmGray4}` : "none",
                      counterIncrement: "ref",
                    }}
                  >
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`product-drawer-reference-${i}`}
                      style={{ fontSize: 14, color: RESEARCH_NAVY, textDecoration: "underline", lineHeight: 1.5 }}
                    >
                      [{i + 1}] {r.title || r.url}
                    </a>
                  </li>
                ))}
              </ol>
            </Section>
          )}
          {Array.isArray(item.attachments) && item.attachments.length > 0 && (
            <Section title="Documents" testId="product-drawer-attachments">
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {item.attachments.map((a, i) => (
                  <a
                    key={i}
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`product-drawer-attachment-${i}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "14px 16px",
                      background: C.pure,
                      border: `1px solid ${C.warmGray4}`,
                      color: RESEARCH_NAVY_DEEP,
                      textDecoration: "none",
                      transition: "border-color 120ms ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = RESEARCH_NAVY)}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.warmGray4)}
                  >
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{a.label || a.url}</div>
                      {a.kind && (
                        <div style={{ fontSize: 10, letterSpacing: "0.2em", color: C.warmGray, textTransform: "uppercase", marginTop: 3 }}>
                          {a.kind}
                        </div>
                      )}
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: RESEARCH_NAVY }}>
                      DOWNLOAD →
                    </span>
                  </a>
                ))}
              </div>
            </Section>
          )}
        </div>

        {/* Sticky action footer */}
        <div
          style={{
            position: "sticky",
            bottom: 0,
            background: C.cream,
            borderTop: `1px solid ${C.warmGray4}`,
            padding: "18px 24px",
            display: "flex",
            gap: 10,
          }}
        >
          {available ? (
            isInTray ? (
              <Button testId={`product-drawer-in-tray-${item.sku}`} variant="ghost" block disabled>
                ✓ ALREADY IN YOUR PLAN
              </Button>
            ) : (
              <Button
                testId={`product-drawer-add-${item.sku}`}
                variant="primary"
                block
                onClick={() => {
                  onAdd?.(item, item.monthlyPrice != null ? "subscription" : "one_time");
                  onClose();
                }}
              >
                ADD TO PLAN →
              </Button>
            )
          ) : (
            <Button
              testId={`product-drawer-notify-${item.sku}`}
              variant="primary"
              block
              onClick={() => onNotify?.(item)}
            >
              NOTIFY ME WHEN AVAILABLE →
            </Button>
          )}
        </div>
      </aside>

      <style>{`
        @keyframes slideInR { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
}

function Section({ title, children, testId }) {
  return (
    <section data-testid={testId} style={{ padding: "22px 0", borderBottom: `1px solid ${C.warmGray4}` }}>
      <h3
        style={{
          margin: "0 0 14px",
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.22em",
          color: RESEARCH_NAVY,
          textTransform: "uppercase",
        }}
      >
        ▪ {title}
      </h3>
      {children}
    </section>
  );
}

// Accept either raw YouTube/Vimeo URLs or a full <iframe> embed URL.
// Falls back to a plain link if we can't detect a video ID.
function VideoEmbed({ url, sku }) {
  const embed = toEmbedUrl(url);
  if (!embed) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`product-drawer-video-fallback-${sku}`}
        style={{ fontSize: 14, color: RESEARCH_NAVY, textDecoration: "underline" }}
      >
        Watch explainer →
      </a>
    );
  }
  return (
    <div
      data-testid={`product-drawer-video-embed-${sku}`}
      style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", background: RESEARCH_NAVY_DEEP }}
    >
      <iframe
        title={`Explainer for ${sku}`}
        src={embed}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
      />
    </div>
  );
}
function toEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    // YouTube — youtu.be/<id> or youtube.com/watch?v=<id> or already /embed/
    if (u.hostname.includes("youtube.com") && u.pathname === "/watch") {
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (u.hostname === "youtu.be") {
      const id = u.pathname.replace("/", "");
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (u.hostname.includes("youtube.com") && u.pathname.startsWith("/embed/")) return url;
    // Vimeo — vimeo.com/<id> or already /video/
    if (u.hostname.includes("vimeo.com") && /^\/\d+/.test(u.pathname)) {
      const id = u.pathname.replace("/", "");
      return `https://player.vimeo.com/video/${id}`;
    }
    if (u.hostname.includes("player.vimeo.com")) return url;
    return null;
  } catch {
    return null;
  }
}

const bodyText = { fontSize: 15, color: RESEARCH_NAVY_DEEP, lineHeight: 1.65, margin: 0 };
